import { Button, Card, Flex, message, theme, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';

import { useHTTP } from '../utils';
import { AxiosError } from 'axios';
import { useClient } from '../client';

export default function EmailVerification({ email }: { email: string | null }) {
  const { token: themeToken } = theme.useToken();
  const [messageApi, contextHolder] = message.useMessage();
  const [resendTimeout, setResendTimeout] = useState<number>(0);
  const client = useClient(null);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setResendTimeout((timeout) => (timeout > 0 ? timeout - 1 : 0));
    }, 20000);
    return () => {
      window.clearInterval(interval);
    };
  }, []);

  const getVerificationEmailTimeout = useCallback(
    async () => {
      const tsStart = Date.now();
      const timeout = await client.getVerificationEmailTimeout();
      const tsDelta = Date.now() - tsStart;
      const correctedTimeout = Math.max(timeout - tsDelta / 2, 0);
      setResendTimeout((prevTimeout) =>
        prevTimeout === 0
          ? correctedTimeout
          : Math.min(correctedTimeout, prevTimeout)
      );
    },
    [client]
  );

  const [_, loadingTimeout, requestEmailTimeout] = useHTTP(
    getVerificationEmailTimeout,
    true,
    'Error',
    messageApi
  );

  const resendEmail = useCallback(
    async () => {
      const timeout = await client.resendVerificationEmail();
      setResendTimeout(timeout);
      messageApi.success('Email sent');
    },
    [messageApi, client]
  );

  const onResendError = useCallback((error: Error) => {
    if (error instanceof AxiosError && error.status === 406) {
      return 'Please, try again later';
    }
    return 'Error';
  }, []);

  const [__, resendingEmail, requestResendEmail] = useHTTP(
    resendEmail,
    false,
    onResendError,
    messageApi
  );

  useEffect(() => {
    const interval1 = window.setInterval(() => {
      setResendTimeout((timeout) => (timeout > 0 ? timeout - 1 : 0));
    }, 1000);
    const interval2 = window.setInterval(requestEmailTimeout, 20000);
    return () => {
      window.clearInterval(interval1);
      window.clearInterval(interval2);
    };
  }, [requestEmailTimeout]);

  return (
    <Flex
      className="auth-container"
      justify="center"
      align="center"
      style={{ backgroundColor: themeToken.colorPrimary }}
    >
      {contextHolder}
      <Card>
        <Typography.Paragraph>
          Verification email has been sent to <b>{email}</b>
        </Typography.Paragraph>
        <Typography.Paragraph>
          Please follow the link in the email to complete your registration
        </Typography.Paragraph>
        <Flex justify="center" align="center">
          <Button
            disabled={
              resendTimeout === null || !!resendTimeout || resendingEmail
            }
            loading={resendingEmail}
            onClick={() => requestResendEmail()}
          >
            Resend {resendTimeout ? `in ${resendTimeout.toFixed(0)} sec` : ''}
          </Button>
        </Flex>
      </Card>
    </Flex>
  );
}
